import type { Transaction } from "@rocicorp/zero";
import { userFacingError } from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { applyGrantDiff, diffGrants } from "./validation/grantContent";

type WriteTx = Pick<Transaction<Schema>, "run" | "mutate">;

// The tenant admins are exactly the user-principal `tenant_admin` grants at the
// tenant scope. A group-conferred grant (one carrying a `userGroupId`) is never
// an admin, so both the SCIM read path and the reconcile below share this one
// predicate rather than each restating the user-principal filter.
export function tenantAdminUserGrants(tenantId: string) {
  return zql.roleGrants
    .where("builtinRoleId", "tenant_admin")
    .where("scopeId", tenantId)
    .where("userId", "IS NOT", null);
}

// Reconciles the tenant admins to exactly `desiredUserIds`. Rejects an empty
// desired set so a tenant can never be left without an admin. Shared by the
// setTenantAdmins UI mutator and the SCIM admin sync so the two can't drift on
// what "the admins are exactly these users" means.
export async function reconcileTenantAdminGrants(
  tx: WriteTx,
  desiredUserIds: readonly string[],
  tenantId: string
): Promise<void> {
  if (desiredUserIds.length === 0) {
    throw userFacingError("There must be at least one tenant admin");
  }
  const existing = await tx.run(tenantAdminUserGrants(tenantId));
  const diff = diffGrants(
    existing,
    desiredUserIds.map((userId) => ({ userId, builtinRoleId: "tenant_admin" }))
  );
  await applyGrantDiff(tx, tenantId, diff, tenantId);
}
